import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Select, InputNumber, Table, Tag, Tabs, Checkbox, Radio, DatePicker } from 'antd';
import { useShippingForm } from './hooks/useShippingForm';
import LookupField from '../../components/LookupField';
import dayjs from 'dayjs';
import { c203Api } from '../../services/c203Api';
import { BatchAcceptanceBridge, pwc203f1AcceptanceManifest } from '../../features/acceptance';

const { TextArea } = Input;

const FREIGHT_OPTIONS = [
  { value: 'P', label: '預付' },
  { value: 'C', label: '到付' },
  { value: 'T', label: '第三方' },
];

const PACK_METHOD_OPTIONS = [
  { value: '01', label: '紙箱' },
  { value: '02', label: '棧板' },
  { value: '03', label: '散裝' },
  { value: '04', label: '其他' },
];

const SHIP_LOC_OPTIONS = [
  { value: 'PW01', label: 'PW01-渤楊總公司' },
  { value: 'PW02', label: 'PW02-渤楊二廠' },
  { value: '01', label: '01-客戶指定' },
  { value: '02', label: '02-其他' },
];

const CONTAINER_TYPE_OPTIONS = [
  { value: 'CFS TO CFS', label: 'CFS TO CFS' },
  { value: 'CFS TO CY', label: 'CFS TO CY' },
  { value: 'CY TO CFS', label: 'CY TO CFS' },
  { value: 'CY TO CY', label: 'CY TO CY' },
];

const DOMESTIC_EXPORT_OPTIONS = [
  { value: 'A', label: 'A-境內出口' },
  { value: 'B', label: 'B-其他' },
];

const C203Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    formData, updateField, importSource,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, submitOrder, withdrawOrder,
    statusLabel, SHIP_TYPE_OPTIONS, INVOICE_OPTIONS,
    lastApiResponse,
  } = useShippingForm();

  const [searchText, setSearchText] = useState('');
  const [sourceType, setSourceType] = useState<'export' | 'domestic'>('export');
  const [sourceOrderNo, setSourceOrderNo] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除出貨單「${current.fc240_01}」？所有明細將一併刪除。`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const canEdit = !current || (current.fc240_80 !== 'P' && current.fc240_80 !== 'C');
  const canSubmit = current && (current.fc240_80 === '' || current.fc240_80 === 'N');
  const canWithdraw = current && current.fc240_80 === 'D';
  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const shipType = formData.fc240_02 || '';

  const detailColumns = [
    { title: <span data-contract-field="fc241_02">項次</span>, dataIndex: 'fc241_02', key: 'seq', width: 60 },
    { title: <span data-contract-field="ui_product_code">產品代號</span>, key: 'productCode', width: 140,
      render: (_: any, r: any) => `${r.fc241_111 || ''}-${r.fc241_112 || ''}` },
    { title: <span data-contract-field="fc241_11">產品規格</span>, dataIndex: 'fc241_11', key: 'spec', width: 160,
      render: (v: string) => <span style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{v || ''}</span> },
    { title: <span data-contract-field="fc241_12">Shipping Mark</span>, dataIndex: 'fc241_12', key: 'mark', width: 140,
      render: (v: string) => <span style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{v || ''}</span> },
    { title: <span data-contract-field="fc241_13">訂單數量</span>, dataIndex: 'fc241_13', key: 'orderQty', width: 100 },
    { title: <span data-contract-field="fc241_16">未出貨數量</span>, dataIndex: 'fc241_16', key: 'unshipped', width: 100 },
    { title: <span data-contract-field="fc241_15">本次出貨量</span>, dataIndex: 'fc241_15', key: 'qty', width: 110 },
    { title: <span data-contract-field="ui_cumulative_shipped_qty">累計出貨量</span>, key: 'cumQty', width: 100,
      render: (_: any, r: any) => ((r.fc241_13 || 0) - (r.fc241_16 || 0)).toFixed(2) },
    { title: <span data-contract-field="fc241_17">單位</span>, dataIndex: 'fc241_17', key: 'unit', width: 70 },
    { title: <span data-contract-field="fc241_18">未稅單價</span>, dataIndex: 'fc241_18', key: 'price', width: 100 },
    { title: <span data-contract-field="fc241_181">含稅單價</span>, dataIndex: 'fc241_181', key: 'priceIncTax', width: 100 },
    { title: <span data-contract-field="fc241_19">未稅金額</span>, dataIndex: 'fc241_19', key: 'amount', width: 110 },
    { title: <span data-contract-field="fc241_191">含稅金額</span>, dataIndex: 'fc241_191', key: 'amountIncTax', width: 110 },
    { title: <span data-contract-field="fc241_20">AS.</span>, dataIndex: 'fc241_20', key: 'as', width: 50,
      render: (v: boolean) => v ? 'Y' : '' },
    { title: <span data-contract-field="fc241_151">補布數量</span>, dataIndex: 'fc241_151', key: 'supplement', width: 90 },
  ];

  const toDayjs = (v: any) => v ? dayjs(v) : null;
  const fromDayjs = (d: any) => d ? d.format('YYYY-MM-DD') : null;

  return (
    <Spin spinning={loading}>
      <div className="max-w-6xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-3">
          出貨單表單 (pwc203f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
            {current && <Tag className="ml-2" color={
              current.fc240_80 === 'P' ? 'green' : current.fc240_80 === 'D' ? 'blue' :
              current.fc240_80 === 'C' ? 'red' : 'default'
            }>{statusLabel(current.fc240_80)}</Tag>}
          </span>
        </h2>

        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入單號或客戶查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)} disabled={isEditing}
            style={{ width: 260 }} maxLength={40} />
          <Button onClick={() => searchText.trim() && window.location.reload()} disabled={isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={navFirst} disabled={isEditing || !records.length}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || !records.length}>&gt;|</Button>
        </div>

        <Tabs defaultActiveKey="1" items={[
          { key: '1', label: '基本資料', children: (
            <div className="grid grid-cols-4 gap-3">
              {/* Row 1: 出貨單號, 出貨類別, 打單日期, 立單人 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_01">出貨單號</label>
                <Input value={formData.fc240_01 || '(自動產生)'} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_02">出貨類別 *</label>
                <Select value={formData.fc240_02} onChange={v => updateField('fc240_02', v)}
                  disabled={!isEditing} options={SHIP_TYPE_OPTIONS} style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_92">打單日期</label>
                <Input value={formData.fc240_92 ? String(formData.fc240_92).substring(0,10) : ''} disabled />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_91">立單人</label>
                <Input value={formData.fc240_91 || ''} disabled />
              </div>

              {/* Row 2: 客戶代號, 訂單NO., 幣別, 匯率 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">客戶代號</label>
                <LookupField lookupType="customer" value={formData.fc240_12 as string || ''}
                  onChange={(code) => updateField('fc240_12', code)}
                  disabled={!isEditing} title="選擇客戶" contractFieldId="fc240_12" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_10">訂單NO.</label>
                <Input value={formData.fc240_10 || ''} disabled />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">幣別</label>
                <LookupField lookupType="currency" value={formData.fc240_18 as string || ''}
                  onChange={(code) => updateField('fc240_18', code)}
                  disabled={!isEditing} title="選擇幣別" contractFieldId="fc240_18" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">匯率 *</label>
                <InputNumber value={formData.fc240_19} onChange={v => updateField('fc240_19', v)}
                  disabled={!isEditing} style={{ width: '100%' }} min={0} step={0.01} />
              </div>

              {/* Conditional fields for non-consignment */}
              {shipType !== '04' && <>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_13">付款條件 *</label>
                  <LookupField lookupType="paymentTerms" value={formData.fc240_13 as string || ''}
                    onChange={(code) => updateField('fc240_13', code)}
                    disabled={!isEditing} title="選擇付款條件" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_131">L/C NO.</label>
                  <LookupField lookupType="lcNumber" value={formData.fc240_131 as string || ''}
                    onChange={(code) => updateField('fc240_131', code)}
                    disabled={!isEditing} title="選擇L/C NO." />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_15">交易條件 *</label>
                  <LookupField lookupType="tradeTerms" value={formData.fc240_15 as string || ''}
                    onChange={(code) => updateField('fc240_15', code)}
                    disabled={!isEditing} title="選擇交易條件" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_16">包裝方式 *</label>
                  <Select value={formData.fc240_16} onChange={v => updateField('fc240_16', v)}
                    disabled={!isEditing} options={PACK_METHOD_OPTIONS} style={{ width: '100%' }}
                    allowClear />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_161">包裝方式說明</label>
                  <Input value={formData.fc240_161 || ''} onChange={e => updateField('fc240_161', e.target.value)}
                    disabled={!isEditing} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_14">出貨地點 *</label>
                  <Select value={formData.fc240_14} onChange={v => updateField('fc240_14', v)}
                    disabled={!isEditing} options={SHIP_LOC_OPTIONS} style={{ width: '100%' }}
                    allowClear />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_141">出貨地址</label>
                  <Input value={formData.fc240_141 || ''} onChange={e => updateField('fc240_141', e.target.value)}
                    disabled={!isEditing} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_17">運費方式 *</label>
                  <Select value={formData.fc240_17} onChange={v => updateField('fc240_17', v)}
                    disabled={!isEditing} options={FREIGHT_OPTIONS} style={{ width: '100%' }}
                    allowClear />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_20">發票種類 *</label>
                  <Select value={formData.fc240_20} onChange={v => updateField('fc240_20', v)}
                    disabled={!isEditing} options={INVOICE_OPTIONS} style={{ width: '100%' }} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_21">指定貨代</label>
                  <LookupField lookupType="supplier" value={formData.fc240_21 as string || ''}
                    onChange={(code) => updateField('fc240_21', code)}
                    disabled={!isEditing} title="選擇貨代" />
                </div>
              </>}

              {/* 公證 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_22">公證要求</label>
                <Checkbox checked={formData.fc240_22} onChange={e => updateField('fc240_22', e.target.checked)}
                  disabled={!isEditing}>公證</Checkbox>
              </div>
              {formData.fc240_22 && <>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">公證行</label>
                  <LookupField lookupType="supplier" value={formData.fc240_221 as string || ''}
                    onChange={(code) => updateField('fc240_221', code)}
                    disabled={!isEditing} title="選擇公證行" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">公證日期</label>
                  <DatePicker value={toDayjs(formData.fc240_222)} onChange={d => updateField('fc240_222', fromDayjs(d))}
                    disabled={!isEditing} style={{ width: '100%' }} />
                </div>
              </>}

              {/* 寄庫庫位 for type 04 */}
              {shipType === '04' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">寄庫庫位 *</label>
                  <LookupField lookupType="warehouse" value={formData.fc240_36 as string || ''}
                    onChange={(code) => updateField('fc240_36', code)}
                    disabled={!isEditing} title="選擇寄庫庫位" />
                </div>
              )}

              {/* 出貨數量(彙總文字), 淨重, 毛重, 材積 */}
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_23">出貨數量</label>
                <TextArea value={formData.fc240_23 || ''} rows={2} disabled />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_232">淨重</label>
                <Input value={current?.fc240_232?.toFixed(4)} disabled />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_233">毛重</label>
                <Input value={current?.fc240_233?.toFixed(4)} disabled />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_231">材積</label>
                <Input value={current?.fc240_231?.toFixed(4)} disabled />
              </div>

              {/* 備註, To關務, To儲運 */}
              <div className="col-span-4">
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_33">備註</label>
                <TextArea value={formData.fc240_33 || ''} rows={2}
                  onChange={e => updateField('fc240_33', e.target.value)} disabled={!isEditing} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_30">To關務</label>
                <TextArea value={formData.fc240_30 || ''} rows={2}
                  onChange={e => updateField('fc240_30', e.target.value)} disabled={!isEditing} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc240_31">To儲運</label>
                <TextArea value={formData.fc240_31 || ''} rows={2}
                  onChange={e => updateField('fc240_31', e.target.value)} disabled={!isEditing} />
              </div>
            </div>
          )},
          { key: '2', label: '產品明細', children: (
            <div>
              {mode === 'add' && (
                <div className="flex items-end gap-2 mb-3 p-3 border rounded bg-gray-50">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">來源類型</label>
                    <Select value={sourceType} onChange={setSourceType} style={{ width: 130 }} options={[
                      { value: 'export', label: '外銷 S/C' },
                      { value: 'domestic', label: '內銷樣品單' },
                    ]} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">來源單號</label>
                    <Input value={sourceOrderNo} onChange={e => setSourceOrderNo(e.target.value)} style={{ width: 220 }} />
                  </div>
                  <Button type="primary" onClick={() => importSource(sourceType, sourceOrderNo)}>帶入明細</Button>
                  <span className="text-xs text-gray-500">先預覽；按儲存後才建立交運單。</span>
                </div>
              )}
              {/* Standalone fields above grid */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc241_10">訂單號碼</label>
                  <Input value={formData.details?.[0]?.fc241_10 || ''} disabled />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1" data-contract-field="fc241_101">項次</label>
                  <Input value={formData.details?.[0]?.fc241_101 || ''} disabled />
                </div>
              </div>
              <Table dataSource={formData.details || []} columns={detailColumns}
                rowKey={(r: any) => `${r.fc241_01}-${r.fc241_02}`}
                size="small" pagination={false} scroll={{ x: 1200, y: 300 }} />
            </div>
          )},
          { key: '3', label: '出貨資訊', children: (
            <div>
              {/* 內銷 type=01 */}
              {shipType === '01' && (
                <div className="mb-4 p-3 border rounded">
                  <h4 className="text-sm font-semibold mb-2">內銷出貨</h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">內銷出貨</label>
                      <Input value={formData.fc240_371 ? String(formData.fc240_371).substring(0,10) : ''}
                        disabled />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">境內出口</label>
                      <Select value={formData.fc240_26} onChange={v => updateField('fc240_26', v)}
                        disabled={!isEditing} options={DOMESTIC_EXPORT_OPTIONS} style={{ width: '100%' }}
                        allowClear />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">境內出口</label>
                      <Input value={formData.fc240_261 || ''} disabled />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">海運</label>
                      <DatePicker value={toDayjs(formData.fc240_262)} onChange={d => updateField('fc240_262', fromDayjs(d))}
                        disabled={!isEditing} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">到達日</label>
                      <DatePicker value={toDayjs(formData.fc240_263)} onChange={d => updateField('fc240_263', fromDayjs(d))}
                        disabled={!isEditing} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">出口港</label>
                      <Input value={formData.fc240_264 || ''} onChange={e => updateField('fc240_264', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">卸貨港</label>
                      <Input value={formData.fc240_265 || ''} onChange={e => updateField('fc240_265', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                  </div>
                </div>
              )}

              {/* 外銷報關 type=02 */}
              {shipType === '02' && (
                <div className="mb-4 p-3 border rounded">
                  <h4 className="text-sm font-semibold mb-2">外銷報關出貨</h4>
                  <div className="grid grid-cols-4 gap-3">
                    {/* 出貨方式選擇 */}
                    <div className="col-span-4">
                      <Radio.Group value={formData.fc240_299} onChange={e => updateField('fc240_299', e.target.value)}
                        disabled={!isEditing}>
                        <Radio value={1}>正常</Radio>
                        <Radio value={2}>異常</Radio>
                      </Radio.Group>
                    </div>
                    <div className="col-span-4 flex gap-4">
                      <Checkbox checked={formData.fc240_27} onChange={e => updateField('fc240_27', e.target.checked)}
                        disabled={!isEditing}>境內出口</Checkbox>
                      <Checkbox checked={formData.fc240_28} onChange={e => updateField('fc240_28', e.target.checked)}
                        disabled={!isEditing}>海運</Checkbox>
                      <Checkbox checked={formData.fc240_29} onChange={e => updateField('fc240_29', e.target.checked)}
                        disabled={!isEditing}>空運</Checkbox>
                    </div>

                    {/* 境內出口 */}
                    {formData.fc240_27 && <>
                      <div className="col-span-4 text-xs font-semibold text-blue-600 mt-1">境內出口報關</div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">出貨方式</label>
                        <Radio.Group value={formData.fc240_271} onChange={e => updateField('fc240_271', e.target.value)}
                          disabled={!isEditing}>
                          <Radio value={1}>海運</Radio>
                          <Radio value={2}>空運</Radio>
                          <Radio value={3}>快遞</Radio>
                        </Radio.Group>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">進倉日</label>
                        <DatePicker value={toDayjs(formData.fc240_272)} onChange={d => updateField('fc240_272', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">快遞出貨</label>
                        <Input value={formData.fc240_273 || ''} onChange={e => updateField('fc240_273', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                    </>}

                    {/* 海運 */}
                    {formData.fc240_28 && <>
                      <div className="col-span-4 text-xs font-semibold text-blue-600 mt-1">海運</div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">出口港</label>
                        <Input value={formData.fc240_281 || ''} onChange={e => updateField('fc240_281', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">卸貨港</label>
                        <Input value={formData.fc240_282 || ''} onChange={e => updateField('fc240_282', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">結關日</label>
                        <DatePicker value={toDayjs(formData.fc240_283)} onChange={d => updateField('fc240_283', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">其它</label>
                        <Input value={formData.fc240_284 || ''} onChange={e => updateField('fc240_284', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">ETD</label>
                        <DatePicker value={toDayjs(formData.fc240_285)} onChange={d => updateField('fc240_285', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">ETA</label>
                        <DatePicker value={toDayjs(formData.fc240_286)} onChange={d => updateField('fc240_286', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">散裝/整櫃</label>
                        <Select value={formData.fc240_287} onChange={v => updateField('fc240_287', v)}
                          disabled={!isEditing} options={CONTAINER_TYPE_OPTIONS} style={{ width: '100%' }}
                          allowClear />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">貨櫃尺寸/櫃數</label>
                        <Input value={formData.fc240_288 || ''} disabled />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">S/O NO.</label>
                        <Input value={formData.fc240_289 || ''} onChange={e => updateField('fc240_289', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">貨櫃場</label>
                        <Input value={formData.fc240_28a || ''} onChange={e => updateField('fc240_28a', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">報關行</label>
                        <Input value={formData.fc240_28b || ''} onChange={e => updateField('fc240_28b', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                    </>}

                    {/* 空運 */}
                    {formData.fc240_29 && <>
                      <div className="col-span-4 text-xs font-semibold text-blue-600 mt-1">空運</div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">出口機場</label>
                        <Input value={formData.fc240_291 || ''} onChange={e => updateField('fc240_291', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">卸貨機場</label>
                        <Input value={formData.fc240_292 || ''} onChange={e => updateField('fc240_292', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">結關日</label>
                        <DatePicker value={toDayjs(formData.fc240_293)} onChange={d => updateField('fc240_293', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">班機航次</label>
                        <Input value={formData.fc240_294 || ''} onChange={e => updateField('fc240_294', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">ETD</label>
                        <DatePicker value={toDayjs(formData.fc240_295)} onChange={d => updateField('fc240_295', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">ETA</label>
                        <DatePicker value={toDayjs(formData.fc240_296)} onChange={d => updateField('fc240_296', fromDayjs(d))}
                          disabled={!isEditing} style={{ width: '100%' }} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">空運理由</label>
                        <Input value={formData.fc240_29a || ''} onChange={e => updateField('fc240_29a', e.target.value)}
                          disabled={!isEditing} />
                      </div>
                    </>}

                    {/* CONSIGNEE / NOTIFY */}
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">CONSIGNEE *</label>
                      <TextArea value={formData.fc240_298 || ''} rows={3}
                        onChange={e => updateField('fc240_298', e.target.value)} disabled={!isEditing} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">NOTIFY</label>
                      <TextArea value={formData.fc240_297 || ''} rows={3}
                        onChange={e => updateField('fc240_297', e.target.value)} disabled={!isEditing} />
                    </div>
                  </div>
                </div>
              )}

              {/* 快遞出貨 type=03 */}
              {shipType === '03' && (
                <div className="mb-4 p-3 border rounded">
                  <h4 className="text-sm font-semibold mb-2">快遞出貨</h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">快遞公司 *</label>
                      <Input value={formData.fc240_351 || ''} onChange={e => updateField('fc240_351', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">進倉日</label>
                      <Input value={formData.fc240_352 || ''} onChange={e => updateField('fc240_352', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">到達日</label>
                      <Input value={formData.fc240_353 || ''} onChange={e => updateField('fc240_353', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">海運</label>
                      <Input value={formData.fc240_354 || ''} onChange={e => updateField('fc240_354', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">出口港</label>
                      <Input value={formData.fc240_355 || ''} onChange={e => updateField('fc240_355', e.target.value)}
                        disabled={!isEditing} />
                    </div>
                  </div>
                </div>
              )}

              {/* 彙總資訊 */}
              <div className="p-3 border rounded">
                <h4 className="text-sm font-semibold mb-2">金額彙總</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs text-gray-600" data-contract-field="fc240_24">未稅總金額</label>
                    <Input value={current?.fc240_24?.toFixed(2)} disabled /></div>
                  <div><label className="text-xs text-gray-600" data-contract-field="fc240_241">含稅總金額</label>
                    <Input value={current?.fc240_241?.toFixed(2)} disabled /></div>
                  <div><label className="text-xs text-gray-600" data-contract-field="fc240_25">本幣金額</label>
                    <Input value={current?.fc240_25?.toFixed(2)} disabled /></div>
                </div>
              </div>
            </div>
          )},
        ]} />

        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mt-3">
            立單人: {current.fc240_91} | 日期: {current.fc240_92 || '-'} | 修改人: {current.fc240_93}
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          {!isEditing ? (<>
            <Button type="primary" onClick={startAdd} data-acceptance-action="PWC203-ACT-CREATE">新增</Button>
            <Button onClick={startEdit} disabled={!current || !canEdit} data-acceptance-action="PWC203-ACT-UPDATE">修改</Button>
            <Button danger onClick={handleDelete} disabled={!current || !canEdit} data-acceptance-action="PWC203-ACT-DELETE">刪除</Button>
            {canSubmit && <Button onClick={submitOrder} data-acceptance-action="PWC203-ACT-SUBMIT">送審</Button>}
            {canWithdraw && <Button onClick={withdrawOrder} data-acceptance-action="PWC203-ACT-WITHDRAW">取回</Button>}
          </>) : (<>
            <Button type="primary" onClick={save}>儲存</Button>
            <Button onClick={cancel}>取消</Button>
          </>)}
        </div>
        <BatchAcceptanceBridge
          manifest={pwc203f1AcceptanceManifest}
          records={records}
          selectedKeys={current ? [current.fc240_01] : []}
          keyOf={(item) => item.fc240_01}
          queryExact={(key) => c203Api.get(key)}
          lastApiResponse={lastApiResponse}
          loading={loading}
          onAutoFill={() => {
            if (records.length > 0) navFirst();
          }}
          autoFillDisabled={records.length === 0}
          autoFillReason="安全載入第一張既有出貨通知單，不會新增、修改、送審或核准。"
          screenDraft={{ mode, master: formData, source_type: sourceType, source_order_no: sourceOrderNo }}
        />
      </div>
    </Spin>
  );
};

export default C203Form;
